import axios from 'axios';
import offlineStorage from './offlineStorage';

const MATCH_FIELDS = [
  'teamA',
  'teamB',
  'scoreA',
  'scoreB',
  'note',
  'time',
  'scoringOrder',
  'timeoutsA',
  'timeoutsB',
  'matchGroup',
  'setNumber',
  'isPublic',
  'isLive',
  'clockBaseSeconds',
  'clockStartedAt',
  'clientId'
];

const SYNC_RETRY_MS = 30000;
const REQUEST_TIMEOUT_MS = 8000;

class SyncService {
  constructor() {
    this.isSyncing = false;
    this.syncCallbacks = [];
    this.retryTimer = null;

    // Auto-sync when the network comes back online.
    offlineStorage.onNetworkChange((status) => {
      if (status === 'online') {
        setTimeout(() => this.syncOfflineMatches(), 2000);
      }
    });

    // Sync on page load once storage is hydrated and we're online.
    offlineStorage.ready.then(() => {
      if (offlineStorage.isOnline()) {
        setTimeout(() => this.syncOfflineMatches(), 1000);
      }
    });

    // Keep trying while the app is open with pending matches (spotty gym Wi‑Fi).
    this.retryTimer = setInterval(() => {
      if (offlineStorage.isOnline() && offlineStorage.getUnsyncedMatches().length > 0) {
        this.syncOfflineMatches();
      }
    }, SYNC_RETRY_MS);

    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible' && offlineStorage.isOnline()) {
        this.syncOfflineMatches();
      }
    });
  }

  onSyncStatusChange(callback) {
    this.syncCallbacks.push(callback);
    return () => {
      this.syncCallbacks = this.syncCallbacks.filter((cb) => cb !== callback);
    };
  }

  notifySyncStatusChange(status) {
    this.syncCallbacks.forEach((callback) => {
      try {
        callback(status);
      } catch (error) {
        console.error('Error in sync status callback:', error);
      }
    });
  }

  // Main sync routine. Safe to call repeatedly: each match carries a clientId
  // so the server dedupes any request that was already applied.
  async syncOfflineMatches(token = null) {
    if (this.isSyncing) {
      return { success: false, message: 'Sync already in progress' };
    }

    await offlineStorage.ready;

    if (!offlineStorage.isOnline()) {
      return { success: false, message: 'No network connection' };
    }

    const unsyncedMatches = offlineStorage.getUnsyncedMatches();
    if (unsyncedMatches.length === 0) {
      return { success: true, message: 'No matches to sync', syncedCount: 0 };
    }

    token = token || localStorage.getItem('token');
    if (!token) {
      this.notifySyncStatusChange({
        status: 'error',
        message: 'No authentication token available',
        syncedCount: 0,
        failedCount: unsyncedMatches.length
      });
      return { success: false, message: 'No authentication token' };
    }

    this.isSyncing = true;
    let syncedCount = 0;
    let failedCount = 0;
    const errors = [];

    this.notifySyncStatusChange({
      status: 'syncing',
      message: `Syncing ${unsyncedMatches.length} matches...`,
      total: unsyncedMatches.length,
      current: 0
    });

    for (let i = 0; i < unsyncedMatches.length; i++) {
      const match = unsyncedMatches[i];

      try {
        this.notifySyncStatusChange({
          status: 'syncing',
          message: `Syncing match ${i + 1} of ${unsyncedMatches.length}: ${match.teamA} vs ${match.teamB}`,
          total: unsyncedMatches.length,
          current: i + 1
        });

        const isUpdate = !!(match.serverId || match.originalMatchId);
        const matchData = this.prepareMatchDataForServer(match, { isUpdate });
        let response;

        if (isUpdate) {
          const matchId = match.serverId || match.originalMatchId;
          response = await axios.put(
            `${process.env.REACT_APP_API_URL}/api/matches/${matchId}`,
            matchData,
            { headers: { Authorization: `Bearer ${token}` }, timeout: REQUEST_TIMEOUT_MS }
          );
        } else {
          response = await axios.post(
            `${process.env.REACT_APP_API_URL}/api/matches`,
            matchData,
            { headers: { Authorization: `Bearer ${token}` }, timeout: REQUEST_TIMEOUT_MS }
          );
        }

        await offlineStorage.markMatchAsSynced(match.offlineId, response.data);
        await offlineStorage.dropSyncedMatch(match.offlineId);
        syncedCount++;

        if (i < unsyncedMatches.length - 1) {
          await this.delay(400);
        }
      } catch (error) {
        failedCount++;
        const errorMessage = this.getErrorMessage(error);

        if (error.response?.status === 401) {
          this.notifySyncStatusChange({
            status: 'error',
            message: 'Authentication expired. Please sign in again to sync.',
            syncedCount,
            failedCount
          });
          break;
        }

        const hasHttpStatus = !!error.response;
        const permanent = hasHttpStatus && error.response.status >= 400 && error.response.status < 500;
        // Timeouts and dropped packets are not a reason to give up.
        const countTowardCap = hasHttpStatus && !permanent;

        await offlineStorage.recordSyncFailure(match.offlineId, errorMessage, {
          permanent,
          countTowardCap
        });

        errors.push({ match: `${match.teamA} vs ${match.teamB}`, error: errorMessage });
      }
    }

    this.isSyncing = false;

    setTimeout(() => offlineStorage.cleanupSyncedMatches(), 1000);

    const result = {
      success: failedCount === 0,
      syncedCount,
      failedCount,
      errors,
      message: this.getSyncResultMessage(syncedCount, failedCount)
    };

    this.notifySyncStatusChange({
      status: result.success ? 'completed' : 'partial',
      message: result.message,
      syncedCount,
      failedCount,
      errors
    });

    return result;
  }

  prepareMatchDataForServer(offlineMatch, { isUpdate = false } = {}) {
    const data = {};
    for (const key of MATCH_FIELDS) {
      if (isUpdate && key === 'clientId') continue;
      if (offlineMatch[key] !== undefined) {
        data[key] = offlineMatch[key];
      }
    }
    return data;
  }

  getErrorMessage(error) {
    if (error.response) {
      if (error.response.status === 401) return 'Authentication failed';
      if (error.response.status === 400) return error.response.data?.message || 'Invalid match data';
      if (error.response.status >= 500) return 'Server error';
      return error.response.data?.message || `HTTP ${error.response.status}`;
    }
    if (error.code === 'ECONNABORTED') return 'Network timeout';
    if (error.request) return 'Network error';
    return error.message || 'Unknown error';
  }

  getSyncResultMessage(syncedCount, failedCount) {
    if (syncedCount === 0 && failedCount === 0) return 'No matches to sync';
    if (failedCount === 0) return `Successfully synced ${syncedCount} match${syncedCount === 1 ? '' : 'es'}`;
    if (syncedCount === 0) return `Failed to sync ${failedCount} match${failedCount === 1 ? '' : 'es'}`;
    return `Synced ${syncedCount}, failed ${failedCount} match${failedCount === 1 ? '' : 'es'}`;
  }

  delay(ms) {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  async triggerManualSync(token) {
    await offlineStorage.ready;
    await offlineStorage.resetFailedMatches();
    return this.syncOfflineMatches(token);
  }

  getSyncStatus() {
    const isOnline = offlineStorage.isOnline();
    const pendingCount = offlineStorage.getPendingCount();
    const failedCount = offlineStorage.getFailedMatches().length;

    return {
      isSyncing: this.isSyncing,
      unsyncedCount: pendingCount,
      failedCount,
      isOnline,
      canSync: isOnline && !this.isSyncing && pendingCount > 0
    };
  }

  async retrySync(token) {
    return this.triggerManualSync(token);
  }
}

const syncService = new SyncService();

export default syncService;
